import { useState, type ReactNode } from 'react'
import type { ScenarioPerson } from './types'

/** Registration mail counts as invited. hasBeenInvited stays the session-invite flag. */
function awaitingFirstInvite(person: ScenarioPerson): boolean {
  return person.status === 'pending' && person.hasBeenInvited === false && person.accountInviteSent !== true
}

function inviteResponseLabel(person: ScenarioPerson): string {
  if (person.status === 'declined') return 'Declined'
  if (person.status === 'joined') return 'Accepted'
  if (awaitingFirstInvite(person)) return 'Not invited yet'
  if (person.status === 'pending') return 'Invited'
  return 'Unknown'
}

/** Instant hover tip (not native title — those delay ~1s). */
export function FastHoverTip({
  tip,
  className = '',
  children,
  onClick,
}: {
  tip: string
  className?: string
  children: ReactNode
  onClick?: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <span
      className={`sc-presence-tip${className ? ` ${className}` : ''}${onClick ? ' is-clickable' : ''}`}
      aria-label={tip}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      {children}
      {open ? (
        <span className="sc-presence-tip-panel" role="tooltip">
          {tip}
        </span>
      ) : null}
    </span>
  )
}

/**
 * Presence pip on a Roster card. Hover tip lists presence + RSVP (instant, not native title).
 */
export function PersonPresencePip({
  person,
  isSelf,
  live = false,
}: {
  person: ScenarioPerson
  isSelf: boolean
  /** Connected to this session right now. */
  live?: boolean
}) {
  const present = isSelf || live
  const presence = present ? 'Present' : 'Away'
  const response = inviteResponseLabel(person)
  const tip = `${presence} · ${response}`

  let kind = 'away'
  if (present) kind = 'present'
  else if (person.status === 'declined') kind = 'declined'
  else if (awaitingFirstInvite(person)) kind = 'listed'
  else if (person.status === 'pending') kind = 'pending'

  const className =
    kind === 'present' ? 'sc-presence' : `sc-presence ${kind === 'away' ? 'away' : kind}`

  return (
    <FastHoverTip tip={tip} className="sc-person-presence-tip">
      <span className={className} aria-hidden="true" />
    </FastHoverTip>
  )
}
